import { HttpClient, HttpParams } from '@angular/common/http';
import { EnvironmentInjector, inject, Injectable, runInInjectionContext } from '@angular/core';
import { CustomEncoder, ENVIRONMENT } from '@iot-platform/core';
import { SortUtil } from '@iot-platform/iot-platform-utils';
import {
  CommonApiListResponse,
  CommonApiRequest,
  CommonApiResponse,
  CommonGenericModel,
  CommonIndexedPagination,
  CommonPagination,
  Environment,
  TagCategory
} from '@iot-platform/models/common';
import {
  BamDeviceButtonColumn,
  BamDeviceTemplatesButtonColumn,
  ColumnFactory,
  DaliaDeviceButtonColumn,
  DaliaDevicesConfigurationButtonColumn,
  DaliaDeviceTemplatesButtonColumn,
  DaliaFirmwareButtonColumn,
  DaliaSensorButtonColumn,
  getAllColumnsByConcept,
  HeaderType,
  I4BAssetButtonColumn,
  I4BAssetEventsButtonColumn,
  I4BAssetTemplatesButtonColumn,
  I4BBasicColumn,
  I4BCalendarsButtonColumn,
  I4BCellType,
  I4BColumn,
  I4BColumnConfiguration,
  I4BColumnHeader,
  I4BColumnOptions,
  I4BConnectorsButtonColumn,
  I4BDeviceButtonColumn,
  I4BDeviceEventsButtonColumn,
  I4BEscalationProtocolsButtonColumn,
  I4BGrid,
  I4BGridData,
  I4BGridFilterOptions,
  I4BGridOptions,
  I4BPoEventRulesButtonColumn,
  I4BProductsButtonColumn,
  I4BSchedulersButtonColumn,
  I4BSelectionColumn,
  I4BSitesButtonColumn,
  I4BStockSiteDevicesButtonColumn,
  I4BTeamPlanningsButtonColumn,
  I4BTopicsButtonColumn,
  I4BUsersButtonColumn,
  KercomDeviceButtonColumn,
  NteDeviceButtonColumn,
  NteDeviceModelsButtonColumn,
  NteDeviceTemplatesButtonColumn,
  NtePeripheralsButtonColumn,
  XmqttDeviceButtonColumn
} from '@iot-platform/models/grid-engine';
import { get } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { resolveAppDefaultGrids } from '../helpers/utils';
import { CustomGridOptions } from '../models/custom-grid-options.model';
import { AbstractGridApiHandlerService } from './abstract-grid-api-handler.service';

@Injectable({
  providedIn: 'root'
})
export class GridsService {
  private readonly environment: Environment = inject(ENVIRONMENT);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly environmentInjector = inject(EnvironmentInjector);

  loadAllGrids(): Observable<CommonApiResponse<I4BGrid<I4BGridOptions, I4BGridData>, CommonIndexedPagination>> {
    return this.http.get<any>(`${this.environment.api.url}/grids`).pipe(
      map((result) => ({
        data: resolveAppDefaultGrids(
          (result.content as I4BGrid<I4BGridOptions, I4BGridData>[]).map((grid) => ({
            ...grid,
            columns: this.getColumnsDefinition(grid),
            gridOptions: this.getGridOptions(grid)
          }))
        ),
        pagination: {
          limit: result.page.limit,
          currentPage: result.page.curPage,
          hasMore: result.page.hasMore,
          maxPage: result.page.maxPage,
          total: result.page.total
        }
      }))
    );
  }

  loadGridDetails(concept: string, gridId: string): Observable<any> {
    return this.http
      .get<any>(`${this.environment.api.url}/grids/${concept}/${gridId}`)
      .pipe(map((grid: I4BGrid<I4BGridOptions, I4BGridData>) => ({ ...grid, columns: this.getColumnsDefinition(grid) })));
  }

  loadGridData(
    request: CommonApiRequest,
    customGridOptions: {
      [masterView: string]: CustomGridOptions;
    }
  ): Observable<CommonApiResponse<CommonGenericModel, CommonPagination>> {
    const httpParams: HttpParams = this.getHttpParams(request);
    let stream$ = this.loadDefault(request, httpParams);
    const gridApiUrlHandlerOptions = get(customGridOptions, request.concept);
    if (gridApiUrlHandlerOptions?.apiHandlerService) {
      const serviceInstance: AbstractGridApiHandlerService = runInInjectionContext(this.environmentInjector, () =>
        inject(gridApiUrlHandlerOptions.apiHandlerService)
      );
      stream$ = serviceInstance.loadGridData({
        ...request,
        httpParams
      });
    }

    return stream$.pipe(
      map(
        (backResponse) =>
          ({
            data: backResponse.content,
            pagination: backResponse?.page
              ? {
                  limit: backResponse?.page?.limit,
                  currentPage: backResponse?.page?.curPage,
                  hasMore: !!backResponse?.page?.hasMore,
                  maxPage: backResponse?.page?.maxPage,
                  total: backResponse?.page?.total,
                  maxCountReached: !!backResponse?.page?.maxCountReached
                }
              : null
          }) as CommonApiResponse<CommonGenericModel, CommonPagination>
      )
    );
  }

  loadGridsByConcept(concept: string): Observable<I4BGrid<I4BGridOptions, I4BGridData>[]> {
    return this.http
      .get<{
        page: unknown;
        content: I4BGrid<I4BGridOptions, I4BGridData>[];
      }>(`${this.environment.api.url}/grids/${concept}`)
      .pipe(
        map((response) =>
          response.content.reduce((acc, value) => {
            const updateGrid = {
              ...value,
              columns: this.getColumnsDefinition(value),
              gridOptions: this.getGridOptions(value)
            };
            acc.push(updateGrid);
            return acc;
          }, [])
        )
      );
  }

  loadGridsByConceptAndBusinessProfile(concept: string, businessProfileId?: string): Observable<I4BGrid<I4BGridOptions, I4BGridData>[]> {
    let params: HttpParams = new HttpParams();
    if (businessProfileId) {
      params = params.set('businessProfileId', businessProfileId);
    }
    return this.http
      .get<{
        page: unknown;
        content: I4BGrid<I4BGridOptions, I4BGridData>[];
      }>(`${this.environment.api.url}/grids/${concept}`, { params })
      .pipe(
        map((response) =>
          response.content.reduce((acc, value) => {
            const updateGrid = {
              ...value,
              columns: this.getColumnsDefinition(value),
              gridOptions: this.getGridOptions(value)
            };
            acc.push(updateGrid);
            return acc;
          }, [])
        )
      );
  }

  saveGrid(newGrid: Partial<I4BGrid<I4BGridOptions, I4BGridData>>) {
    return this.http
      .post<any>(`${this.environment.api.url}/grids/${newGrid.masterview}`, {
        ...newGrid,
        data: null
      })
      .pipe(
        map((grid: I4BGrid<I4BGridOptions, I4BGridData>) => ({
          ...grid,
          columns: this.getColumnsDefinition(grid),
          gridOptions: this.getGridOptions(grid)
        }))
      );
  }

  updateGrid(updatedGrid: Partial<I4BGrid<I4BGridOptions, I4BGridData>>) {
    const isAppDefault = updatedGrid.isDefault && !updatedGrid.userId;
    if (isAppDefault) {
      return this.http
        .put<any>(`${this.environment.api.url}/grids/${updatedGrid.masterview}/default/app`, {
          ...updatedGrid,
          data: null
        })
        .pipe(
          map((grid: I4BGrid<I4BGridOptions, I4BGridData>) => ({
            ...grid,
            columns: this.getColumnsDefinition(grid),
            gridOptions: this.getGridOptions(grid)
          }))
        );
    } else {
      return this.http
        .put<any>(`${this.environment.api.url}/grids/${updatedGrid.masterview}/${updatedGrid.id}`, {
          ...updatedGrid,
          data: null
        })
        .pipe(
          map((grid: I4BGrid<I4BGridOptions, I4BGridData>) => ({
            ...grid,
            columns: this.getColumnsDefinition(grid),
            gridOptions: this.getGridOptions(grid)
          }))
        );
    }
  }

  updateSilentGrid(updatedGrid: Partial<I4BGrid<I4BGridOptions, I4BGridData>>) {
    const isUserGrid = !updatedGrid.isAppDefault;
    if (isUserGrid) {
      return this.http
        .put<any>(`${this.environment.api.url}/grids/${updatedGrid.masterview}/${updatedGrid.id}`, {
          ...updatedGrid,
          data: null
        })
        .pipe(
          map((grid: I4BGrid<I4BGridOptions, I4BGridData>) => ({
            ...grid,
            columns: this.getColumnsDefinition(grid),
            gridOptions: this.getGridOptions(grid)
          }))
        );
    }
  }

  deleteGrid(toDeleteGrid: Partial<I4BGrid<I4BGridOptions, I4BGridData>>) {
    return this.http.delete<any>(`${this.environment.api.url}/grids/${toDeleteGrid.masterview.toLowerCase()}/${toDeleteGrid.id}`);
  }

  loadTagsByConceptsAndEntity(concepts: string[], entityId: string): Observable<TagCategory[]> {
    let params: HttpParams = new HttpParams();
    concepts.forEach((concept) => {
      params = params.append('concept', concept);
    });
    params = params.set('entityId', entityId);
    params = params.set('withParents', true);
    params = params.set('withChildren', true);
    return this.http
      .get<CommonApiListResponse<TagCategory>>(`${this.environment.api.url}${this.environment.api.endpoints.tags}`, { params })
      .pipe(map((response) => response.content.sort(SortUtil.sortByName)));
  }

  getDefaultGridByConcept(concept: string) {
    return this.http.get<any>(`${this.environment.api.url}/grids/${concept}/default`).pipe(
      map((grid: I4BGrid<I4BGridOptions, I4BGridData>) => ({
        ...grid,
        columns: this.getColumnsDefinition(grid),
        gridOptions: this.getGridOptions(grid)
      }))
    );
  }

  getAppDefaultGridByConcept(concept: string): Observable<I4BGrid<I4BGridOptions, I4BGridData>> {
    return this.http.get<any>(`${this.environment.api.url}/grids/${concept}/default/app`).pipe(
      map((grid: I4BGrid<I4BGridOptions, I4BGridData>) => ({
        ...grid,
        columns: this.getColumnsDefinition(grid),
        gridOptions: this.getGridOptions(grid)
      }))
    );
  }

  getHttpParams(request: CommonApiRequest) {
    let params: HttpParams = new HttpParams({ encoder: new CustomEncoder() });
    if (request.limit !== null && request.limit !== undefined && request.limit !== -1) {
      params = params.set('limit', request.limit.toString(10));
      params = params.set('page', request.page.toString(10));
    }

    if (request.filters) {
      request.filters.forEach((filter) => {
        if (filter.criteriaOperator === I4BGridFilterOptions.IN_RANGE_START) {
          params = params.append(`${filter.criteriaKey}Start`, filter.value);
        } else if (filter.criteriaOperator === I4BGridFilterOptions.IN_RANGE_END) {
          params = params.append(`${filter.criteriaKey}End`, filter.value);
        } else {
          params = params.append(filter.criteriaKey, filter.value);
        }
        if (filter.criteriaOperatorKey && !params.get(filter.criteriaOperatorKey)) {
          params = params.append(filter.criteriaOperatorKey, filter.criteriaOperator);
        }
        if (filter.criteriaOperatorStartKey && !params.get(filter.criteriaOperatorStartKey)) {
          params = params.append(filter.criteriaOperatorStartKey, filter.criteriaOperatorStart);
        }
        if (filter.criteriaOperatorEndKey && !params.get(filter.criteriaOperatorEndKey)) {
          params = params.append(filter.criteriaOperatorEndKey, filter.criteriaOperatorEnd);
        }
      });
    }

    if (request.variables && request.variables.length > 0) {
      request.variables.forEach((variable) => {
        params = params.append('expandVariableName', variable.toLowerCase());
      });
    }

    if (request.tags && request.tags.length > 0) {
      request.tags.forEach((tagCategory) => {
        params = params.append('expandTagCategory', tagCategory);
      });
    }
    return params;
  }

  // TODO to be removed once new filter engine system will be implemented
  loadDefault(request: CommonApiRequest, params: HttpParams): Observable<CommonApiListResponse<CommonGenericModel>> {
    return this.http.get<CommonApiListResponse<CommonGenericModel>>(
      `${this.environment.api.url}${request.endPoint ? request.endPoint : '/' + request.concept}`,
      {
        params
      }
    );
  }

  private getGridOptions(grid: I4BGrid<I4BGridOptions, I4BGridData>): I4BGridOptions {
    // TODO : on doit largement pouvoir optimiser ça !
    switch (grid.masterview) {
      case 'sites':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BSitesButtonColumn() },
          selectionColumn: { enabled: true, className: new I4BSelectionColumn() }
        };
      case 'assets':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BAssetButtonColumn() },
          selectionColumn: { enabled: true, className: new I4BSelectionColumn() }
        };
      case 'devices':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BDeviceButtonColumn() },
          selectionColumn: { enabled: true, className: new I4BSelectionColumn() }
        };
      case 'dalia-devices':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new DaliaDeviceButtonColumn() },
          selectionColumn: { enabled: false, className: new I4BSelectionColumn() }
        };
      case 'xmqtt-devices':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new XmqttDeviceButtonColumn() },
          selectionColumn: { enabled: true, className: new I4BSelectionColumn() }
        };
      case 'nte-devices':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new NteDeviceButtonColumn() },
          selectionColumn: { enabled: true, className: new I4BSelectionColumn() }
        };
      case 'nte-device-models':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: false, className: new NteDeviceModelsButtonColumn() },
          selectionColumn: { enabled: false, className: new I4BSelectionColumn() }
        };
      case 'nte-peripherals':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new NtePeripheralsButtonColumn() },
          selectionColumn: { enabled: true, className: new I4BSelectionColumn() }
        };
      case 'nte-device-templates':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new NteDeviceTemplatesButtonColumn() },
          selectionColumn: { enabled: false, className: new I4BSelectionColumn() }
        };
      case 'bam-devices':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new BamDeviceButtonColumn() },
          selectionColumn: { enabled: true, className: new I4BSelectionColumn() }
        };
      case 'bam-device-templates':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new BamDeviceTemplatesButtonColumn() },
          selectionColumn: { enabled: false, className: new I4BSelectionColumn() }
        };
      case 'kercom-v2-devices':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new KercomDeviceButtonColumn() },
          selectionColumn: { enabled: true, className: new I4BSelectionColumn() }
        };
      case 'dalia-firmwares':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: false, className: new DaliaFirmwareButtonColumn() },
          selectionColumn: { enabled: false, className: new I4BSelectionColumn() }
        };
      case 'dalia-device-templates':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new DaliaDeviceTemplatesButtonColumn() },
          selectionColumn: { enabled: false, className: new I4BSelectionColumn() }
        };
      case 'dalia-devices-configuration':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new DaliaDevicesConfigurationButtonColumn() },
          selectionColumn: { enabled: false, className: new I4BSelectionColumn() }
        };
      case 'dalia-sensors':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: false, className: new DaliaSensorButtonColumn() },
          selectionColumn: { enabled: false, className: new I4BSelectionColumn() }
        };
      case 'device-events':
      case 'device-events-by-pe-rule':
      case 'device-events-by-topic':
      case 'device-events-by-site':
      case 'device-events-by-device':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BDeviceEventsButtonColumn() },
          selectionColumn: { enabled: true, className: new I4BSelectionColumn() }
        };
      case 'asset-events':
      case 'active-asset-events-popup':
      case 'asset-events-by-pe-rule':
      case 'asset-events-by-topic':
      case 'asset-events-by-site':
      case 'asset-events-by-asset':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BAssetEventsButtonColumn() },
          selectionColumn: { enabled: true, className: new I4BSelectionColumn() }
        };
      case 'connectors':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BConnectorsButtonColumn() }
        };
      case 'asset-templates':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BAssetTemplatesButtonColumn() }
        };
      case 'stock-site-devices':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BStockSiteDevicesButtonColumn() },
          selectionColumn: { enabled: true, className: new I4BSelectionColumn() }
        };
      case 'users':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BUsersButtonColumn() }
        };
      case 'products':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BProductsButtonColumn() }
        };
      case 'escalation-protocols':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BEscalationProtocolsButtonColumn() }
        };
      case 'topics':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BTopicsButtonColumn() }
        };
      case 'po-event-rules':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BPoEventRulesButtonColumn() }
        };
      case 'calendars':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BCalendarsButtonColumn() }
        };
      case 'team-plannings':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BTeamPlanningsButtonColumn() }
        };
      case 'schedulers':
        return {
          ...grid.gridOptions,
          buttonColumn: { enabled: true, className: new I4BSchedulersButtonColumn() }
        };
      default:
        return { ...grid.gridOptions };
    }
  }

  private getColumnsDefinition(grid: I4BGrid<I4BGridOptions, I4BGridData>): I4BColumn<I4BColumnHeader, I4BColumnConfiguration, I4BColumnOptions>[] {
    return grid.columns.map((column: I4BColumn<I4BColumnHeader, I4BColumnConfiguration, I4BColumnOptions>) => this.getConsolidatedColumn(column, grid));
  }

  private getConsolidatedColumn(
    userColumn: I4BColumn<I4BColumnHeader, I4BColumnConfiguration, I4BColumnOptions>,
    grid: I4BGrid<I4BGridOptions, I4BGridData>
  ): I4BColumn<I4BColumnHeader, I4BColumnConfiguration, I4BColumnOptions> {
    const columnConfig = ColumnFactory.getUserColumnClass(userColumn.columnId);
    let consolidatedColumn: I4BBasicColumn;
    if (columnConfig.options.allowUserOverride) {
      // AllowUserOverride : all the conf comes from the back. No consolidation here,
      consolidatedColumn = ColumnFactory.createColumn(columnConfig.class, userColumn.header, userColumn.configuration, userColumn.options);
    } else {
      if (columnConfig.options && columnConfig.options.allowSystemOverride) {
        // systemOverride : the conf come from the developer (in default-config-definition file. Consolidation needed with the customization of the user (like header name) and developer configuration
        // so, we need to look up in the columns catalog to retrieve default conf and apply customization.
        const defaultViewDefCols = getAllColumnsByConcept(grid.masterview.toLowerCase()).filter((viewDefColumn) =>
          userColumn?.options?.customId
            ? viewDefColumn.columnId === userColumn?.columnId && viewDefColumn.configuration?.id === userColumn?.options?.customId
            : userColumn?.configuration?.id
              ? viewDefColumn.columnId === userColumn?.columnId && viewDefColumn.configuration?.id === userColumn?.configuration?.id
              : false
        );
        const defaultViewDefCol: I4BColumn<I4BColumnHeader, I4BColumnConfiguration, I4BColumnOptions> | undefined = get(defaultViewDefCols, '[0]');
        if (defaultViewDefCol) {
          consolidatedColumn = ColumnFactory.createColumn(
            defaultViewDefCol.className,
            {
              ...defaultViewDefCol?.header,
              ...userColumn?.header
            },
            {
              ...defaultViewDefCol?.configuration,
              ...userColumn?.configuration
            },
            {
              ...userColumn.options
            }
          );
        } else {
          consolidatedColumn = ColumnFactory.createColumn(columnConfig.class, {}, {}, userColumn.options);
        }
      } else {
        consolidatedColumn = ColumnFactory.createColumn(columnConfig.class, {}, {}, userColumn.options);
      }
    }

    if ((consolidatedColumn.header.type as string) === 'basic') {
      consolidatedColumn.header.type = HeaderType.BASIC;
    }

    switch (consolidatedColumn.configuration.cell.type as string) {
      case 'basic':
        consolidatedColumn.configuration.cell.type = I4BCellType.BASIC;
        break;
      case 'number':
        consolidatedColumn.configuration.cell.type = I4BCellType.NUMBER;
        break;
      case 'date':
        consolidatedColumn.configuration.cell.type = I4BCellType.DATE;
        break;
    }
    return consolidatedColumn;
  }
}
